import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bot,
  Brain,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  DollarSign,
  FileText,
  Inbox,
  Loader2,
  Phone,
  Search,
  Send,
  Shield,
  Sparkles,
  TrendingUp,
  Users,
  Zap,
  Activity,
  PhoneCall,
  MessageSquare,
  Database,
  FileCheck2,
  Mail,
  CreditCard,
  RefreshCcw,
  BadgeCheck,
} from "lucide-react";

const prompts = [
  {
    text: "Run all my insurance verifications",
    agent: "Amanda",
    role: "Insurance Verification",
    color: "from-blue-500 to-indigo-600",
    accent: "blue",
    image: "/assets/amanda.png",
    icon: Shield,
    reasoning: [
      { icon: Calendar, text: "Pulling tomorrow's schedule — 24 patients." },
      { icon: Database, text: "Matching each patient to their carrier + plan." },
      { icon: PhoneCall, text: "Querying 6 insurance APIs in parallel…" },
      { icon: FileCheck2, text: "Confirming deductibles, max used, and coverage tiers." },
      { icon: BadgeCheck, text: "All 24 records verified. 3 flagged for front-desk review." },
    ],
  },
  {
    text: "Take care of all my claims for me",
    agent: "Alayna",
    role: "Claim Scrubbing",
    color: "from-violet-500 to-purple-600",
    accent: "violet",
    image:
      "https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png",
    icon: FileText,
    reasoning: [
      { icon: Inbox, text: "Loaded 63 unsent claims from today's production." },
      { icon: Search, text: "Auditing CDT codes, narratives, and attachments." },
      { icon: RefreshCcw, text: "Auto-correcting 11 common scrubber errors." },
      { icon: Send, text: "Submitting 52 clean claims to carriers…" },
      { icon: ClipboardCheck, text: "Queued 11 edge cases in the billing review inbox." },
    ],
  },
  {
    text: "Fill my hygiene schedule this week",
    agent: "Stephanie",
    role: "Hygiene Recall",
    color: "from-amber-500 to-orange-600",
    accent: "amber",
    image: "/assets/stephanie.png",
    icon: Users,
    reasoning: [
      { icon: Users, text: "Found 184 patients overdue by 30+ days." },
      { icon: Search, text: "Ranking by insurance activity + open benefits." },
      { icon: MessageSquare, text: "Sending personalized SMS + email to top 60." },
      { icon: Calendar, text: "Booking 22 replies into open hygiene slots." },
      { icon: TrendingUp, text: "Filled Tue/Wed/Thu — Fri has 3 slots remaining." },
    ],
  },
  {
    text: "Post all my EOBs automatically",
    agent: "Alisha",
    role: "EOB Posting",
    color: "from-emerald-500 to-teal-600",
    accent: "emerald",
    image: "/assets/alisha.png",
    icon: CheckCircle2,
    reasoning: [
      { icon: Mail, text: "Pulling 41 EOBs from ERA + carrier portals." },
      { icon: FileCheck2, text: "Parsing line items, adjustments, and write-offs." },
      { icon: Database, text: "Matching payments to open ledger entries." },
      { icon: DollarSign, text: "Posting $18,420 across 214 procedures." },
      { icon: BadgeCheck, text: "Reconciled. 2 underpayments flagged for appeal." },
    ],
  },
  {
    text: "Call insurance companies for me",
    agent: "Laura",
    role: "Insurance Calls",
    color: "from-cyan-500 to-blue-600",
    accent: "cyan",
    image:
      "https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png",
    icon: Phone,
    reasoning: [
      { icon: ClipboardCheck, text: "Queue: 14 calls for eligibility + claim status." },
      { icon: PhoneCall, text: "Dialing Delta Dental… on hold, 8m." },
      { icon: MessageSquare, text: "Navigating IVR, authenticating as office." },
      { icon: FileText, text: "Capturing rep name, ref #, and full quote." },
      { icon: Database, text: "14/14 complete. Notes synced to patient charts." },
    ],
  },
  {
    text: "Call patients to confirm appointments",
    agent: "Amy",
    role: "Receptionist",
    color: "from-rose-500 to-pink-600",
    accent: "rose",
    image: "/assets/amy.png",
    icon: Calendar,
    reasoning: [
      { icon: Calendar, text: "Tomorrow's roster: 28 appointments across 3 ops." },
      { icon: PhoneCall, text: "Calling each patient in priority order." },
      { icon: MessageSquare, text: "Re-confirming, answering questions, collecting COB." },
      { icon: RefreshCcw, text: "Filling 2 cancellations from the waitlist live." },
      { icon: BadgeCheck, text: "24 confirmed, 2 rescheduled, 2 filled — schedule solid." },
    ],
  },
  {
    text: "Handle all denied claims and appeals",
    agent: "Sarah",
    role: "Denial Management",
    color: "from-indigo-500 to-violet-600",
    accent: "indigo",
    image: "/assets/sarah.png",
    icon: TrendingUp,
    reasoning: [
      { icon: Inbox, text: "Pulled 19 denials from last 30 days." },
      { icon: Search, text: "Classifying by denial code + carrier pattern." },
      { icon: FileText, text: "Drafting 14 appeals with narrative + attachments." },
      { icon: Send, text: "Submitted via payer portal + fax fallback." },
      { icon: DollarSign, text: "Projected recovery: $11,340. 5 require chart notes." },
    ],
  },
  {
    text: "Collect on all outstanding balances",
    agent: "Julia",
    role: "A/R Collections",
    color: "from-green-500 to-emerald-600",
    accent: "green",
    image: "/assets/julia.png",
    icon: Activity,
    reasoning: [
      { icon: Database, text: "Scanned AR — $42,810 across 96 patient balances." },
      { icon: Search, text: "Segmenting by age bucket + prior contact history." },
      { icon: CreditCard, text: "Sending text-to-pay links to 61 patients." },
      { icon: PhoneCall, text: "Escalating 14 accounts >90d to live dialer." },
      { icon: TrendingUp, text: "Collected $8,920 so far today. Queue still active." },
    ],
  },
];

const PMSOfferingsSlide = ({ onExplore }) => {
  const [currentPrompt, setCurrentPrompt] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [phase, setPhase] = useState("typing");
  const [agentTasks, setAgentTasks] = useState([]);
  const [reasoningStep, setReasoningStep] = useState(0);

  useEffect(() => {
    const prompt = prompts[currentPrompt];
    let charIndex = 0;
    let typingTimer;
    let phaseTimer;
    let stepTimer;

    if (phase === "typing") {
      setDisplayText("");
      setReasoningStep(0);
      typingTimer = setInterval(() => {
        charIndex++;
        setDisplayText(prompt.text.slice(0, charIndex));
        if (charIndex >= prompt.text.length) {
          clearInterval(typingTimer);
          phaseTimer = setTimeout(() => setPhase("responding"), 400);
        }
      }, 35);
    } else if (phase === "responding") {
      setAgentTasks((prev) =>
        [
          {
            id: Date.now(),
            agent: prompt.agent,
            role: prompt.role,
            text: prompt.text,
            color: prompt.color,
            icon: prompt.icon,
            image: prompt.image,
          },
          ...prev,
        ].slice(0, 3)
      );
      setReasoningStep(0);
      stepTimer = setInterval(() => {
        setReasoningStep((s) => {
          if (s + 1 >= prompt.reasoning.length) {
            clearInterval(stepTimer);
            phaseTimer = setTimeout(() => setPhase("complete"), 600);
            return s + 1;
          }
          return s + 1;
        });
      }, 650);
    } else if (phase === "complete") {
      phaseTimer = setTimeout(() => setPhase("pause"), 1800);
    } else if (phase === "pause") {
      phaseTimer = setTimeout(() => {
        setCurrentPrompt((prev) => (prev + 1) % prompts.length);
        setPhase("typing");
      }, 400);
    }

    return () => {
      clearInterval(typingTimer);
      clearInterval(stepTimer);
      clearTimeout(phaseTimer);
    };
  }, [currentPrompt, phase]);

  const currentPromptData = prompts[currentPrompt];
  const totalSteps = currentPromptData.reasoning.length;
  const isWorking = phase === "responding";
  const isDone = phase === "complete";

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col items-center justify-center p-6 xl:p-8 font-sans relative overflow-hidden">
      {/* Ambient tints */}
      <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-blue-100/50 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />

      <div className="max-w-7xl w-full h-full flex flex-col justify-center relative z-10">
        {/* Title */}
        <div className="text-center mb-5 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-5xl font-extrabold text-slate-900 mb-3 tracking-tight leading-[1.1]">
              The World's 1st <span className="text-blue-600">AI Native PMS</span>: where{" "}
              <span className="text-blue-600">20+ AI employees</span> run your practice on autopilot.
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed">
              Asha's software replaces{" "}
              <span className="font-semibold text-slate-900">10 of your existing softwares</span>, and Asha's{" "}
              <span className="font-semibold text-slate-900">20+ AI agents</span> perform the 40+ tasks that weigh down your staff today.
            </p>
          </motion.div>
        </div>

        {/* Demo shell */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="max-w-[1150px] mx-auto w-full flex-1 min-h-0 flex flex-col"
        >
          <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-blue-400/60 via-indigo-400/30 to-transparent shadow-[0_30px_80px_-20px_rgba(59,130,246,0.25)] flex-1 min-h-0 flex">
            <div className="relative rounded-[15px] overflow-hidden flex flex-col flex-1 min-h-0 bg-white">
              {/* Soft tints */}
              <div className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-blue-100/60 rounded-full blur-[120px] pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 w-[360px] h-[360px] bg-indigo-100/60 rounded-full blur-[100px] pointer-events-none" />
              <div
                className="absolute inset-0 opacity-[0.5] pointer-events-none"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(226,232,240,1) 1px, transparent 1px), linear-gradient(90deg, rgba(226,232,240,1) 1px, transparent 1px)",
                  backgroundSize: "32px 32px",
                  maskImage:
                    "radial-gradient(ellipse at center, black 40%, transparent 75%)",
                }}
              />

              {/* Top bar */}
              <div className="relative z-10 flex items-center justify-between px-5 py-2.5 border-b border-slate-100 flex-shrink-0 bg-white/80 backdrop-blur-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-medium bg-slate-50 text-slate-600 border border-slate-200">
                  <Bot className="w-3.5 h-3.5 text-blue-500" />
                  <span>Asha PMS</span>
                  <div className="flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_6px_rgba(16,185,129,0.8)]" />
                    AI Active
                  </div>
                </div>
                <div className="w-16" />
              </div>

              {/* Body */}
              <div className="relative z-10 flex flex-1 min-h-0">
                {/* LEFT: command + activity */}
                <div className="flex-1 min-w-0 flex flex-col p-4 md:p-5 gap-4 border-r border-slate-100">
                  {/* Command bar */}
                  <div
                    className={`relative rounded-xl border overflow-hidden transition-all duration-500 flex-shrink-0 ${
                      phase === "typing"
                        ? "border-blue-300 shadow-[0_0_28px_-8px_rgba(59,130,246,0.45)] bg-blue-50/40"
                        : isWorking || isDone
                        ? "border-emerald-300 shadow-[0_0_28px_-8px_rgba(16,185,129,0.45)] bg-emerald-50/40"
                        : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-3 px-4 py-3 bg-white/70">
                      <div
                        className={`relative w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gradient-to-br ${currentPromptData.color} shadow-[0_6px_16px_-6px_rgba(59,130,246,0.6)]`}
                      >
                        <div className="absolute inset-0 rounded-lg bg-white/10" />
                        <Sparkles className="w-4 h-4 text-white relative" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.14em] font-bold mb-0.5 text-blue-600/80">
                          Ask your AI team anything
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-medium text-slate-900">
                            {displayText}
                          </span>
                          {phase === "typing" && (
                            <motion.span
                              animate={{ opacity: [1, 0] }}
                              transition={{ duration: 0.5, repeat: Infinity }}
                              className="w-0.5 h-5 bg-blue-500 inline-block"
                            />
                          )}
                        </div>
                      </div>
                      <motion.div
                        animate={{
                          scale: phase === "typing" ? [1, 1.08, 1] : 1,
                        }}
                        transition={{ duration: 0.3 }}
                        className={`relative w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                          isWorking || isDone
                            ? "bg-gradient-to-br from-emerald-400 to-teal-500 shadow-[0_6px_16px_-4px_rgba(16,185,129,0.55)]"
                            : "bg-gradient-to-br from-blue-500 to-indigo-600 shadow-[0_6px_16px_-4px_rgba(59,130,246,0.55)]"
                        }`}
                      >
                        {isWorking ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: "linear",
                            }}
                          >
                            <Zap className="w-4 h-4 text-white" />
                          </motion.div>
                        ) : isDone ? (
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        ) : (
                          <Send className="w-4 h-4 text-white" />
                        )}
                      </motion.div>
                    </div>
                  </div>

                  {/* Activity feed */}
                  <div className="flex flex-col flex-1 min-h-0">
                    <div className="flex items-center gap-2 mb-2 flex-shrink-0">
                      <Bot className="w-4 h-4 text-blue-500" />
                      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                        Recent AI Activity
                      </span>
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-bold flex items-center gap-1 border border-blue-200">
                        <Sparkles className="w-2.5 h-2.5" /> Autonomous
                      </span>
                    </div>
                    <div className="space-y-2 flex-1 overflow-hidden">
                      <AnimatePresence mode="popLayout">
                        {agentTasks.map((task) => (
                          <motion.div
                            key={task.id}
                            initial={{ opacity: 0, x: -20, scale: 0.95 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            layout
                            className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-200 bg-white shadow-sm"
                          >
                            <div className="relative shrink-0">
                              <img
                                src={task.image}
                                alt={task.agent}
                                className="w-8 h-8 rounded-full object-cover ring-2 ring-blue-100"
                              />
                              <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-2 border-white shadow-[0_0_6px_rgba(59,130,246,0.5)]">
                                <Sparkles className="w-2 h-2 text-white" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-sm font-bold text-slate-900">
                                  {task.agent}
                                </span>
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold uppercase tracking-wider flex items-center gap-0.5 border border-blue-200">
                                  <Bot className="w-2.5 h-2.5" /> AI
                                </span>
                                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-600 border border-slate-200 truncate">
                                  {task.role}
                                </span>
                              </div>
                              <div className="text-xs text-slate-600 truncate">
                                "{task.text}" —{" "}
                                <span className="text-emerald-700 font-medium">
                                  completed
                                </span>
                              </div>
                            </div>
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                          </motion.div>
                        ))}
                      </AnimatePresence>
                      {agentTasks.length === 0 && (
                        <div className="flex flex-col items-center justify-center h-full rounded-xl border border-dashed border-slate-200 text-slate-400">
                          <Bot className="w-8 h-8 mb-2 opacity-40" />
                          <span className="text-sm">
                            AI agents waiting for instructions...
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* RIGHT: reasoning flyout */}
                <div className="w-[420px] flex-shrink-0 flex flex-col bg-gradient-to-b from-slate-50/80 to-white">
                  {/* Flyout header */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 flex-shrink-0 bg-white/60 backdrop-blur-sm">
                    <div className="w-6 h-6 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center">
                      <Brain className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600">
                      Agent Reasoning
                    </span>
                    <div className="ml-auto flex items-center gap-1.5">
                      {isWorking ? (
                        <>
                          <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />
                          <span className="text-[10px] font-bold text-blue-600">
                            Thinking…
                          </span>
                        </>
                      ) : isDone ? (
                        <>
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                          <span className="text-[10px] font-bold text-emerald-600">
                            Done
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                          <span className="text-[10px] font-bold text-slate-400">
                            Idle
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Agent header card */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`head-${currentPromptData.agent}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25 }}
                      className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 flex-shrink-0"
                    >
                      <div className="relative shrink-0">
                        <img
                          src={currentPromptData.image}
                          alt={currentPromptData.agent}
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-blue-100"
                        />
                        {isWorking && (
                          <motion.div
                            className="absolute inset-0 rounded-full border-2 border-blue-400/60"
                            animate={{ scale: [1, 1.15, 1], opacity: [0.7, 0, 0.7] }}
                            transition={{ duration: 1.6, repeat: Infinity }}
                          />
                        )}
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-2 border-white shadow-[0_0_6px_rgba(59,130,246,0.5)]">
                          <Sparkles className="w-2 h-2 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-bold text-slate-900">
                            {currentPromptData.agent}
                          </span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold uppercase tracking-wider border border-blue-200">
                            AI Agent
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 font-medium truncate">
                          {currentPromptData.role}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                          Step
                        </div>
                        <div className="text-sm font-black text-slate-900 tabular-nums">
                          {Math.min(reasoningStep, totalSteps)}
                          <span className="text-slate-400">/{totalSteps}</span>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  {/* Reasoning steps */}
                  <div className="flex-1 min-h-0 overflow-hidden px-4 py-3">
                    <div className="relative h-full">
                      {/* Vertical thread */}
                      <div className="absolute left-[14px] top-1 bottom-1 w-px bg-gradient-to-b from-blue-200 via-slate-200 to-transparent" />

                      <div className="space-y-2.5 relative">
                        {currentPromptData.reasoning.map((step, i) => {
                          const StepIcon = step.icon;
                          const revealed = i < reasoningStep;
                          const active = i === reasoningStep - 1 && isWorking;
                          const done = i < reasoningStep - 1 || (i < reasoningStep && isDone);
                          return (
                            <motion.div
                              key={`${currentPromptData.agent}-${i}`}
                              initial={{ opacity: 0, x: 8 }}
                              animate={{
                                opacity: revealed ? 1 : 0.25,
                                x: 0,
                              }}
                              transition={{ duration: 0.25 }}
                              className="flex items-start gap-3 relative"
                            >
                              {/* Node */}
                              <div className="relative flex-shrink-0 z-10">
                                <motion.div
                                  animate={
                                    active
                                      ? { scale: [1, 1.08, 1] }
                                      : { scale: 1 }
                                  }
                                  transition={{ duration: 1.2, repeat: active ? Infinity : 0 }}
                                  className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-colors ${
                                    done
                                      ? "bg-emerald-50 border-emerald-200"
                                      : active
                                      ? "bg-blue-50 border-blue-300 shadow-[0_0_12px_-2px_rgba(59,130,246,0.5)]"
                                      : revealed
                                      ? "bg-blue-50 border-blue-200"
                                      : "bg-slate-50 border-slate-200"
                                  }`}
                                >
                                  {done ? (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                  ) : active ? (
                                    <Loader2 className="w-3.5 h-3.5 text-blue-600 animate-spin" />
                                  ) : (
                                    <StepIcon
                                      className={`w-3.5 h-3.5 ${
                                        revealed ? "text-blue-600" : "text-slate-400"
                                      }`}
                                    />
                                  )}
                                </motion.div>
                              </div>
                              {/* Text */}
                              <div className="flex-1 min-w-0 pt-0.5">
                                <p
                                  className={`text-[12.5px] leading-snug transition-colors ${
                                    revealed
                                      ? "text-slate-800 font-medium"
                                      : "text-slate-400"
                                  }`}
                                >
                                  {step.text}
                                </p>
                                {active && (
                                  <div className="flex gap-1 mt-1">
                                    <motion.span
                                      animate={{ opacity: [0.3, 1, 0.3] }}
                                      transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                                      className="w-1 h-1 rounded-full bg-blue-500"
                                    />
                                    <motion.span
                                      animate={{ opacity: [0.3, 1, 0.3] }}
                                      transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                                      className="w-1 h-1 rounded-full bg-blue-500"
                                    />
                                    <motion.span
                                      animate={{ opacity: [0.3, 1, 0.3] }}
                                      transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                                      className="w-1 h-1 rounded-full bg-blue-500"
                                    />
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Flyout footer */}
                  <div className="px-4 py-2.5 border-t border-slate-100 flex items-center gap-2 flex-shrink-0 bg-white/60 backdrop-blur-sm">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <motion.div
                        initial={false}
                        animate={{
                          width: `${(Math.min(reasoningStep, totalSteps) / totalSteps) * 100}%`,
                        }}
                        transition={{ duration: 0.3 }}
                        className={`h-full rounded-full ${
                          isDone
                            ? "bg-gradient-to-r from-emerald-400 to-teal-500"
                            : "bg-gradient-to-r from-blue-500 to-indigo-600"
                        }`}
                      />
                    </div>
                    <span className="text-[10px] font-bold text-slate-500 tabular-nums">
                      {Math.round(
                        (Math.min(reasoningStep, totalSteps) / totalSteps) * 100
                      )}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PMSOfferingsSlide;
